import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import WaykeEcomWeb from '@wayke-se/ecom-web';
import { marked } from 'marked';
import i18next from 'i18next';
import Container from '../../StyledComponents/Container';
import UspList, { ItemProps } from '../../StyledComponents/UspList';
import { Repeat, RepeatSmall } from '../../StyledComponents/Repeat';
import PriceTable from '../../StyledComponents/PriceTable';
import LogoBox from '../../StyledComponents/LogoBox';
import Content from '../../StyledComponents/Content';
import Blockquote from '../../StyledComponents/Blockquote';
import ExtendContent from '../../StyledComponents/ExtendContent';
import Gallery from '../../StyledComponents/Gallery';
import { Page, PageSection } from '../../StyledComponents/Page';
import {
  ProductPage,
  ProductPageMainSection,
  ProductPageAside,
  ProductPageMain,
  ProductPageAsideSection,
} from '../../StyledComponents/ProductPage';
import { H1, H2 } from '../../StyledComponents/Heading';
import { PortalNamespace, PortalElement } from '../../StyledComponents/Portal';
import FinancialOptions from './FinancialOptions';
import InsuranceOptions from './InsuranceOptions';
import useSearchItem from './Hooks/useSearchItem';
import { notEmpty, numberSeparator } from '../../utils/formats';
import { dateTimeFormat } from '../../utils/dates';
import ManufacturerPackageOption from './ManufacturerPackagesOption';
import CheckList from './CheckList';
import Related from './Related';
import PackageOptions from './PackageOptions';
import Branch from './Branch';
import useCentralStorage from './State/CentralStorage/useCentralStorage';
import Page404 from './Page404';
import PageLoading from './PageLoading';
import PubSub from '../../utils/PubsubItem';
import Property from './Property';
import useSettings from './State/Settings/useSettings';
import EvCalculation from './EvCalculation';
import DemoCarModal from './DemoCarModal';
import { ConversionOption } from '../../@types/Search';
import Documents from './Documents';
import AccessoriesSection from './Accessories';
import useAd from '../../Gql/ad/useAd';
import { legacyWaykeIdFromIterationId } from '../../Gql/ad/legacyWaykeId';

export interface WaykeSearchItemProps {
  id: string;
  evCalculatorUrl: string;
  conversionOptions?: ConversionOption[];
  disableResetScrollOnInit?: boolean;
  placeholderImage?: string;
  modifyDocumentTitleItem?: boolean;
  evCalculatorEnabled?: boolean;
  /** Base path of vehicle links, e.g. /sok/id/ */
  itemHref?: string;
  onClickSearchItem?: (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    id: string,
    slug: string
  ) => void;
}

const WaykeSearchItem = ({
  id,
  evCalculatorUrl,
  conversionOptions,
  disableResetScrollOnInit,
  placeholderImage,
  modifyDocumentTitleItem,
  evCalculatorEnabled,
  itemHref,
  onClickSearchItem,
}: WaykeSearchItemProps) => {
  const settings = useSettings();
  const { publicApi } = settings;
  const ecomContext = useRef<WaykeEcomWeb | undefined>(undefined);

  const [demoCarModal, setDemoCarModal] = useState(false);
  const onToggleDemoCarModal = useCallback(() => setDemoCarModal(!demoCarModal), [demoCarModal]);

  const legacy = useSearchItem(publicApi ? undefined : id);
  const ad = useAd(id, publicApi);
  const loading = publicApi ? ad.loading : legacy.loading;
  const result = publicApi
    ? ad.result
      ? { vehicle: ad.result.vehicle }
      : undefined
    : legacy.data;
  const { vehicle: centralStorageVehicle, loading: loadingCentralStorageVehicle } =
    useCentralStorage(result?.vehicle);

  // An old link's legacy id is resolved by useAd; the public API is queried by ad.id.
  const adId = publicApi ? ad.result?.ad.id || id : id;
  // Ecom and ev-calculation are keyed on the legacy waykeId, not on ad.id.
  const waykeId = publicApi
    ? ad.result &&
      legacyWaykeIdFromIterationId(ad.result.ad.iteration?.id, ad.result.ad.id)
    : id;

  useEffect(() => {
    if (settings.ecomSettings && waykeId) {
      ecomContext.current = new WaykeEcomWeb({
        id: waykeId,
        ecomSdkConfig: {
          api: {
            address: settings.ecomSettings?.url as string,
          },
          bankIdThumbprint: settings.ecomSettings?.bankIdThumbprint,
        },
        logo: settings.ecomSettings.serviceLogotypeUrl,
        logoX2: settings.ecomSettings.serviceLogotypeUrlX2,
      });
    }
    return () => {
      if (ecomContext.current) {
        ecomContext.current.destroy();
        ecomContext.current = undefined;
      }
    };
  }, [waykeId]);

  useEffect(() => {
    if (!disableResetScrollOnInit) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (result?.vehicle?.data && modifyDocumentTitleItem) {
      const { vehicle } = result;
      const { registrationNumber } = vehicle.data;
      document.title = `${registrationNumber} | ${vehicle.title} | ${vehicle.shortDescription}`;
    }
  }, [result]);

  const options = useMemo(
    () => result?.vehicle?.data?.options?.filter(notEmpty).map((opt) => ({ title: opt })),
    [result?.vehicle?.data?.options]
  );

  const onShowMoreOptionsClick = useCallback(() => PubSub.publish('OptionsClick'), []);

  if (loading) {
    return <PageLoading />;
  }

  if (!result?.vehicle?.data) {
    return <Page404 />;
  }

  const { vehicle } = result;
  const contact = centralStorageVehicle?.contact;
  const branch = centralStorageVehicle?.branch;
  const {
    accessories,
    title,
    shortDescription,
    media,
    description,
    price,
    deductibleVat,
    discount,
    financialOptions,
    manufacturer,
    ecommerce,
    packageOptions,
    publishedAt,
    availableFrom,
    flags,
    documents,
  } = vehicle;
  const insuranceOptions = centralStorageVehicle?.insuranceOptions
    ? centralStorageVehicle.insuranceOptions
    : vehicle.insuranceOptions;
  const { fuelType, mileage, odometerReading, gearboxType, modelYear, propertySet } = vehicle.data;

  const uspList: ItemProps[] = [
    {
      title: modelYear,
    },
  ];

  if (flags?.demoVersion) {
    uspList.push({
      title: i18next.t('item.demoCar'),
      onClick: onToggleDemoCarModal,
    });
  }

  uspList.push({
    title: `${numberSeparator(odometerReading?.value || mileage)} ${i18next.t(
      `odometer.${odometerReading?.unit || 'ScandinavianMile'}`
    )}`,
  });

  if (gearboxType) {
    uspList.push({ title: gearboxType });
  }

  if (fuelType) {
    uspList.push({ title: fuelType });
  }

  return (
    <>
      <Page>
        <PageSection large>
          <Container>
            <ProductPage>
              <ProductPageAside>
                <ProductPageAsideSection mobileOrder={1}>
                  {manufacturer?.logotype && (
                    <Repeat>
                      <LogoBox logo={manufacturer.logotype} alt={manufacturer.name} />
                    </Repeat>
                  )}
                  <Repeat>
                    <RepeatSmall>
                      <H1 noMargin>{title}</H1>
                    </RepeatSmall>
                    {shortDescription && (
                      <RepeatSmall>
                        <div>{shortDescription}</div>
                      </RepeatSmall>
                    )}
                    <RepeatSmall>
                      <UspList small items={uspList} />
                    </RepeatSmall>
                  </Repeat>
                </ProductPageAsideSection>

                <ProductPageAsideSection mobileOrder={2}>
                  <PriceTable price={price} discount={discount || undefined} priceExclVat={deductibleVat ? price * 0.8 : undefined} />
                </ProductPageAsideSection>

                <ProductPageAsideSection mobileOrder={4}>
                  <CheckList
                    manufacturer={manufacturer}
                    vehicle={vehicle}
                    packageOptions={packageOptions}
                    ecommerce={ecommerce}
                    branch={branch}
                    contact={centralStorageVehicle?.contact}
                    conversionOptions={conversionOptions}
                    availableFrom={availableFrom}
                    loadingCentralStorageVehicle={loadingCentralStorageVehicle}
                    startEcom={() => ecomContext.current?.start()}
                  />
                </ProductPageAsideSection>

                {(financialOptions.length > 0 || insuranceOptions.length > 0) && (
                  <ProductPageAsideSection mobileOrder={5}>
                    {financialOptions.length > 0 && (
                      <FinancialOptions id={adId} financialOptions={financialOptions} />
                    )}
                    {insuranceOptions.length > 0 && (
                      <InsuranceOptions
                        id={adId}
                        branch={centralStorageVehicle?.branch}
                        insuranceOptions={insuranceOptions}
                      />
                    )}
                  </ProductPageAsideSection>
                )}

              </ProductPageAside>
              <ProductPageMain>
                <ProductPageAsideSection mobileOrder={3}>
                  <Gallery media={media} placeholderImage={placeholderImage} />
                </ProductPageAsideSection>

                <ProductPageMainSection>
                  <Repeat>
                    <H2 noMargin>{i18next.t('item.carData')}</H2>
                  </Repeat>
                  <Repeat>
                    <Content>
                      <p>{i18next.t('item.carDataDescription')}</p>
                    </Content>
                  </Repeat>
                  <Repeat>
                    <Property
                        propertySet={propertySet}
                        vehicleData={vehicle.data}
                        tree={ad.result?.propertyTree}
                      />
                  </Repeat>
                </ProductPageMainSection>

                {description && (
                  <ProductPageMainSection>
                    <Blockquote
                      author={contact && contact.name ? contact.name : null}
                      date={
                        publishedAt
                          ? dateTimeFormat.format(publishedAt, dateTimeFormat.DayMonth)
                          : undefined
                      }
                      avatar={contact?.avatar || undefined}
                    >
                      {!!description && (
                        <div dangerouslySetInnerHTML={{ __html: marked(description) }} />
                      )}
                    </Blockquote>
                  </ProductPageMainSection>
                )}

                {(options?.length || 0) > 0 && (
                  <ProductPageMainSection>
                    <Repeat>
                      <H2 noMargin>{i18next.t('item.equipment')}</H2>
                    </Repeat>
                    <Repeat>
                      <Content>
                        <p>{i18next.t('item.equipmentDescription')}</p>
                      </Content>
                    </Repeat>
                    <Repeat>
                      <ExtendContent
                        actionTitle={i18next.t('common.showMore')}
                        onClick={onShowMoreOptionsClick}
                      >
                        <UspList items={options} />
                      </ExtendContent>
                    </Repeat>
                  </ProductPageMainSection>
                )}
                {evCalculatorEnabled && fuelType === 'El' && (
                  <ProductPageMainSection>
                    <EvCalculation id={waykeId || id} evCalculatorUrl={evCalculatorUrl} />
                  </ProductPageMainSection>
                )}

                <Documents documents={documents} />

                <AccessoriesSection accessories={accessories} />

                <ProductPageMainSection>
                  <Branch branch={branch} loading={loadingCentralStorageVehicle} />
                </ProductPageMainSection>

                <PackageOptions packageOptions={packageOptions} />
                <ManufacturerPackageOption packageOption={manufacturer?.packageOption} />
              </ProductPageMain>
            </ProductPage>
          </Container>
        </PageSection>
        {result.vehicle.id && result.vehicle.branch?.id && (
          <Related
            id={result.vehicle.id}
            branchId={result.vehicle.branch.id}
            manufacturer={result.vehicle.data.manufacturer}
            href={itemHref}
            onClickSearchItem={onClickSearchItem}
          />
        )}
      </Page>
      <PortalElement id={PortalNamespace.DefaultPortal} />
      {demoCarModal && <DemoCarModal onClose={onToggleDemoCarModal} />}
    </>
  );
};

export default WaykeSearchItem;
